import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { offset, flip, shift } from '@floating-ui/react-dom-interactions';
import classNameBind from 'classnames/bind';
import Caption from '../Caption';
import positionOptions from '../../utils/methods/positionOptions';
import uuid from '../../utils/methods/uuid';
import styles from './Tooltip.module.css';

const Tooltip = ({
  children,
  content,
  title,
  caption,
  triggerOnClick,
  hideSpeechBubble,
  position,
  dataId,
  preventHideOnMouseDown,
}) => {
  const [tooltipContainer, setTooltipContainer] = useState(null);
  const [tooltipId, setTooltipId] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef(null);
  const bindClasses = classNameBind.bind(styles);

  useEffect(() => {
    const existingTooltipPortal = document.querySelector('.tooltipPortal');

    if (!existingTooltipPortal) {
      const tooltipPortal = document.createElement('div');
      tooltipPortal.classList.add('tooltipPortal');
      document.body.appendChild(tooltipPortal);
      setTooltipContainer(tooltipPortal);
    } else {
      setTooltipContainer(existingTooltipPortal);
    }

    setTooltipId(`pp-tooltip-${uuid()}`);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tooltipRef]);

  useEffect(() => {
    if (tooltipRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            setIsOpen(false);
          }
        },
        { threshold: 1.0 },
      );

      observer.observe(tooltipRef.current);

      return () => observer.disconnect();
    }

    return undefined;
  }, [isOpen]);

  const config = classNames => ({
    'data-tooltip-id': tooltipId,
    onDragStart: () => { setIsOpen(false); },
    onMouseDown: (preventHideOnMouseDown && triggerOnClick) || triggerOnClick || (!triggerOnClick && preventHideOnMouseDown) ? () => { setIsOpen(true); } : () => { setIsOpen(false); },
    onMouseOver: !triggerOnClick ? () => { setIsOpen(true); } : undefined,
    onMouseLeave: !triggerOnClick ? () => { setIsOpen(false); } : undefined,
    className: classNames.trim() === 'undefined' ? '' : classNames,
    ref: tooltipRef,
  });

  const allowedTypes = ['string', 'number'];

  const addTooltipAttributes = (element) => {
    if (!React.isValidElement(element) && !allowedTypes.includes(typeof element)) return element;

    if (typeof element.type === 'string') {
      return React.cloneElement(element, config(`${element.props.className}`));
    }

    if (typeof element.type === 'function' || allowedTypes.includes(typeof element)) {
      return (
        <div {...config(bindClasses('wrapper'))} data-test-id="Tooltip">
          {element}
        </div>
      );
    }

    return element;
  };

  return (
    <>
      {React.Children.map(children, addTooltipAttributes)}
      {(caption || title || content) && tooltipContainer && isOpen && createPortal(
        <ReactTooltip
          className={`${bindClasses('tooltip', `tooltip-position-${position}`, { isOpen })}`}
          id={tooltipId}
          openOnClick={triggerOnClick}
          noArrow={hideSpeechBubble}
          isOpen={isOpen}
          place={position}
          middlewares={[
            !hideSpeechBubble ? offset(8) : offset(0),
            flip(),
            shift(),
          ]}
        >
          {caption && <div className={bindClasses('tooltip-caption')} data-ppuc-id={`${dataId}.caption`}><Caption subduedDarkBg>{caption}</Caption></div>}
          {title && <div className={bindClasses('tooltip-title')} data-ppuc-id={`${dataId}.title`}>{title}</div>}
          {content && <div className={bindClasses('tooltip-content')} data-ppuc-id={`${dataId}.content`}>{content}</div>}
        </ReactTooltip>,
        tooltipContainer,
      )}
    </>
  );
};

Tooltip.defaultProps = {
  caption: null,
  title: null,
  content: null,
  dataId: 'Tooltip',
  position: 'top',
  hideSpeechBubble: false,
  preventHideOnMouseDown: false,
  triggerOnClick: false,
};

Tooltip.propEnums = {
  position: positionOptions,
};

export default Tooltip;
